import { useIntl } from 'react-intl';
import { Waves } from 'lucide-react';
import { foundation } from '../../ui/tokens';
import { poolTypeDisplayLabel } from '../../utils/customer/poolTypeLabel';

/**
 * Field-facing `customers.pool_type` annotation — the ONE renderer for pool type
 * outside the admin edit drawer. Before this, the column was write-only: captured
 * in the drawer + CSV import, stored, exported, fed to the AI, and shown to nobody.
 *
 * SELF-GATING on both inputs — it returns null when the customer has no pool type
 * OR when `isPoolIndustry` is false, so every call site stays a single line and no
 * surface can drift from another. The industry gate is load-bearing, not cosmetic:
 * `pool_type` doubles as a free-text "site details" field for non-pool tenants (see
 * CustomerEditDrawer's `poolUi` branch), so rendering it unconditionally would drop
 * a paragraph of prose into a chip. `isPoolIndustry` arrives as a PROP rather than
 * from `useAuth()` on purpose — reading context here would pull AuthContext, and
 * with it the Supabase client, into every tree that renders a route card.
 *
 * Two variants — both are icon + label with NO container; they differ only in the
 * rhythm of their host (type scale, icon size, and whether a bottom margin is
 * needed). Neither draws a fill, border, or pill:
 *   `chip` — inline in the Log Job hero row · worker Next Stop hero
 *   `row`  — inside a meta stack (route-card address/phone rows)
 *
 * Color is `neutral.textSecondary` (#475569), NOT `neutral.textMuted` (#94a3b8): this
 * is read on a phone in direct sun, and the muted token is ~2.6:1 on white (fails WCAG AA).
 */
export default function PoolTypeTag({ poolType, isPoolIndustry = false, variant = 'chip', style }) {
  const intl = useIntl();

  if (!poolType || !isPoolIndustry) return null;

  const label = poolTypeDisplayLabel(poolType, intl);

  return (
    <span
      style={{ ...(variant === 'row' ? rowStyle : chipStyle), ...style }}
      aria-label={intl.formatMessage(
        { id: 'customers.poolTypeValue', defaultMessage: 'Pool type: {value}' },
        { value: label },
      )}
      data-testid="pool-type-tag"
    >
      <Waves size={variant === 'row' ? 13 : 12} strokeWidth={2} aria-hidden style={{ flexShrink: 0 }} />
      <span>{label}</span>
    </span>
  );
}

// Icon + label only — deliberately NO fill, border, or pill padding.
//
// On the Log Job hero it sits beside the Follow-up (indigo) and Off-season
// (amber) pills, and those are EXCEPTIONAL flags: something is unusual about
// this visit. Pool type is standing context that is true every visit forever.
// Giving it the same pill shape made permanent background sit at the same
// visual weight as a live flag. Stripping the container keeps the distinction
// the neighbouring copy already claims — pills mean "look at this", plain text
// means "here's what this pool is".
const chipStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 5,
  fontFamily: foundation.fontFamily.sans,
  fontSize: foundation.fontSize.xs,
  fontWeight: foundation.fontWeight.semibold,
  color: foundation.color.neutral.textSecondary,
  // 1.2 matches the canonical pill token's lineHeight — this chip sits beside
  // the Follow-up/Off-season pills on the Log Job hero and shares their
  // baseline rhythm even though it draws no box of its own.
  lineHeight: 1.2,
  whiteSpace: 'nowrap',
};

// Sits in the route card's meta stack beside the address + phone rows — same
// 0.8rem / 600 / 4px-rhythm those use, muted instead of linked because it isn't tappable.
const rowStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 5,
  margin: '0 0 4px 0',
  fontFamily: foundation.fontFamily.sans,
  fontSize: '0.8rem',
  fontWeight: foundation.fontWeight.semibold,
  color: foundation.color.neutral.textSecondary,
};
